import {
  EmptyState,
  LoaderIcon,
  Typography,
  FontWeightEnum,
  FontSizeEnum,
  IconSizeEnum,
} from '@acceso/design-system';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useProgramsByIds } from '@/features/programs/hooks/useProgramsByIds';

import { TClip } from '../../types';

import { ClipMiniCardBase } from './ClipMiniCardBase/ClipMiniCardBase';

type TClipBodyCardListProps = {
  items: TClip[];
  clipsData: UseInfiniteQueryResult<InfiniteData<TClip[], unknown>, Error>;
};

export function ClipBodyCardList({ items, clipsData }: TClipBodyCardListProps) {
  const { activeClip, isHeaderVisible } = useBroadcastScheduleProgram();
  const { programsById } = useProgramsByIds(items.map(item => item.program_id));
  const { t } = useTranslation();
  const observerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && clipsData?.hasNextPage && !clipsData?.isFetchingNextPage) {
        clipsData.fetchNextPage();
      }
    },
    [clipsData?.hasNextPage, clipsData?.isFetchingNextPage, clipsData?.fetchNextPage]
  );

  useEffect(() => {
    if (!observerRef.current) return;

    const options = {
      root: scrollContainerRef.current,
      rootMargin: '0px 0px 400px 0px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver(handleIntersection, options);
    const currentObserverRef = observerRef.current;

    observer.observe(currentObserverRef);

    return () => {
      if (currentObserverRef) {
        observer.unobserve(currentObserverRef);
      }
      observer.disconnect();
    };
  }, [handleIntersection, items.length]);

  return (
    <div
      ref={scrollContainerRef}
      className={clsx(
        'flex min-h-[400px] flex-col overflow-y-auto',
        isHeaderVisible ? 'lg:max-h-[calc(100vh-280px)]' : 'lg:max-h-[calc(100vh-188px)]'
      )}
    >
      {items.map(item => (
        <ClipMiniCardBase
          key={item.id}
          item={item}
          program={programsById.get(item.program_id)}
          isActive={activeClip?.id === item.id}
        />
      ))}
      {items.length === 0 && <EmptyState title={t('No hay clips disponibles')} />}
      <div ref={observerRef} className="h-40" />

      {clipsData?.isFetchingNextPage && (
        <div className="my-4 flex w-full items-center justify-center">
          <Typography weight={FontWeightEnum.Semibold} fontSize={FontSizeEnum.Sm}>
            <LoaderIcon size={IconSizeEnum['2XS']} />
          </Typography>
        </div>
      )}
    </div>
  );
}
